import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../../store/store';
import type { DriveDocumentListItem } from '../types';

export interface DocumentsState {
  byId: Record<string, DriveDocumentListItem>;
  ids: string[];
  selectedDocumentId: string | null;
}

const createEmptyState = (): DocumentsState => ({ byId: {}, ids: [], selectedDocumentId: null });

const sortDocuments = (docs: DriveDocumentListItem[]): DriveDocumentListItem[] => {
  return [...docs].sort((a, b) => {
    const scoreA = a.relevanceScore ?? 0;
    const scoreB = b.relevanceScore ?? 0;
    if (scoreA !== scoreB) return scoreB - scoreA;
    const timeA = a.modifiedTime ? new Date(a.modifiedTime).getTime() : 0;
    const timeB = b.modifiedTime ? new Date(b.modifiedTime).getTime() : 0;
    return timeB - timeA;
  });
};

export const documentsSlice = createSlice({
  name: 'documents',
  initialState: createEmptyState(),
  reducers: {
    upsertDocuments: (state, action: PayloadAction<DriveDocumentListItem[]>) => {
      const incoming = action.payload.filter(
        (doc): doc is DriveDocumentListItem =>
          typeof doc?.documentId === 'string' && doc.documentId.trim().length > 0
      );

      if (incoming.length === 0) return;

      const nextById: Record<string, DriveDocumentListItem> = { ...state.byId };
      for (const doc of incoming) {
        nextById[doc.documentId] = doc;
      }

      const sorted = sortDocuments(Object.values(nextById));
      state.byId = nextById;
      state.ids = sorted.map((doc) => doc.documentId);
    },
    setDocuments: (state, action: PayloadAction<DriveDocumentListItem[]>) => {
      const docs = action.payload.filter(
        (doc): doc is DriveDocumentListItem =>
          typeof doc?.documentId === 'string' && doc.documentId.trim().length > 0
      );
      const sorted = sortDocuments(docs);
      const byId: Record<string, DriveDocumentListItem> = {};
      for (const doc of sorted) {
        byId[doc.documentId] = doc;
      }
      state.byId = byId;
      state.ids = sorted.map((doc) => doc.documentId);
    },
    clearDocuments: (state) => {
      state.byId = {};
      state.ids = [];
    },
    setSelectedDocumentId: (state, action: PayloadAction<string | null>) => {
      state.selectedDocumentId = action.payload;
    },
    selectDocument: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!id) return;
      state.selectedDocumentId = id;
    },
    deselectDocument: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedDocumentId === id) {
        state.selectedDocumentId = null;
      }
    },
    clearSelectedDocuments: (state) => {
      state.selectedDocumentId = null;
    },
  },
});

export const {
  upsertDocuments,
  setDocuments,
  clearDocuments,
  setSelectedDocumentId,
  selectDocument,
  deselectDocument,
  clearSelectedDocuments,
} = documentsSlice.actions;

export const selectDocumentsState = (state: RootState) => state.documents;

export const selectDocumentsOrdered = (state: RootState): DriveDocumentListItem[] => {
  const slice = selectDocumentsState(state);
  return slice.ids
    .map((id: string) => slice.byId[id])
    .filter((doc): doc is DriveDocumentListItem => Boolean(doc));
};

export const selectSelectedDocumentId = (state: RootState): string | null => {
  return selectDocumentsState(state).selectedDocumentId;
};

export const selectSelectedDocument = (state: RootState): DriveDocumentListItem | null => {
  const slice = selectDocumentsState(state);
  const id = slice.selectedDocumentId;
  return id ? (slice.byId[id] ?? null) : null;
};

export const selectSelectedDocuments = (state: RootState): DriveDocumentListItem[] => {
  const doc = selectSelectedDocument(state);
  return doc ? [doc] : [];
};

export default documentsSlice.reducer;
